/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable jsx-a11y/label-has-for */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Icon from 'components/Icon/Icon';
import { clientActions, modalActions } from 'actions';
import { normalizeText } from 'helpers';

// Constants
const DEBOUNCE_DELAY = 150;

/**
 * Custom hook for debounced search
 * @param {string} value - The value to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {string} - The debounced value
 */
const useDebouncedValue = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Custom hook for click outside detection
 * @param {React.RefObject} ref - The ref to monitor
 * @param {Function} handler - The handler to call when clicking outside
 */
const useClickOutside = (ref, handler) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        handler();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref, handler]);
};

/**
 * Highlight matching text in search results.
 * Handles accented characters correctly (e.g., searching "cafe" highlights "Café").
 * Uses normalizeText utility for accent-insensitive matching.
 */
const highlightMatch = (text, searchTerm) => {
  if (!searchTerm) return text;
  const normalizedSearch = normalizeText(searchTerm);
  const normalizedText = normalizeText(text);
  const index = normalizedText.indexOf(normalizedSearch);
  if (index === -1) return text;

  // Find the actual end position in original text
  // This handles cases where accented chars in original text have different
  // normalized lengths (e.g., "é" normalizes to "e" but is 1 char in original)
  let originalEndIndex = index;
  let normalizedCount = 0;
  while (normalizedCount < normalizedSearch.length && originalEndIndex < text.length) {
    const char = text[originalEndIndex];
    const normalizedChar = normalizeText(char);
    normalizedCount += normalizedChar.length;
    originalEndIndex += 1;
  }

  const before = text.slice(0, index);
  const match = text.slice(index, originalEndIndex);
  const after = text.slice(originalEndIndex);

  return (
    <>
      {before}
      <mark style={{ backgroundColor: '#fff59d', padding: 0 }}>{match}</mark>
      {after}
    </>
  );
};

/**
 * Restrictions component for managing client manufacturer exclusions
 * Allows users to select manufacturers to exclude from a client's catalog
 */
const Restrictions = ({
  readOnly,
  id,
  clientRestrictions,
  updateState,
  // Redux props
  selectedPlatform,
  manufacturersAvailable,
  manufacturersLoading,
  manufacturersError,
  fetchClientManufacturers,
  openConfirmModal,
}) => {
  // Local state
  const [searchInputValue, setSearchInputValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isRestrictionEnabled, setIsRestrictionEnabled] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // Debounced search term
  const searchTerm = useDebouncedValue(searchInputValue, DEBOUNCE_DELAY);

  // Refs
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const listContainerRef = useRef(null);
  const prevPropsRef = useRef({ id, selectedPlatform, clientRestrictions });

  // Close dropdown when clicking outside
  useClickOutside(
    dropdownRef,
    useCallback(() => setIsDropdownOpen(false), [])
  );

  // Fetch manufacturers for current client/platform
  const fetchManufacturers = useCallback(() => {
    const clientId = Number(id);
    const platformId = Number(selectedPlatform);

    if (!Number.isFinite(clientId) || clientId <= 0) return;
    if (!Number.isFinite(platformId) || platformId <= 0) return;

    fetchClientManufacturers(clientId, platformId);
  }, [id, selectedPlatform, fetchClientManufacturers]);

  // Initial fetch on mount
  useEffect(() => {
    fetchManufacturers();
  }, [fetchManufacturers]);

  // Handle platform/client changes - reset state
  useEffect(() => {
    const prevId = prevPropsRef.current.id;
    const prevPlatform = prevPropsRef.current.selectedPlatform;

    if (Number(prevId) !== Number(id) || Number(prevPlatform) !== Number(selectedPlatform)) {
      // Reset local UI state when platform/client changes
      setIsRestrictionEnabled(null);
      setSearchInputValue('');
      setIsDropdownOpen(false);
      setFocusedIndex(-1);
    }

    prevPropsRef.current = { id, selectedPlatform, clientRestrictions };
  }, [id, selectedPlatform, clientRestrictions]);

  // Detect when restrictions have been cleared after save
  useEffect(() => {
    const prevRestrictions = prevPropsRef.current.clientRestrictions;

    if (prevRestrictions !== clientRestrictions) {
      const currentRestriction = (clientRestrictions || []).find(
        (r) => r.platform && Number(r.platform.id) === Number(selectedPlatform)
      );
      const prevRestriction = (prevRestrictions || []).find(
        (r) => r.platform && Number(r.platform.id) === Number(selectedPlatform)
      );

      const currentManufacturers =
        (currentRestriction && currentRestriction.excluded_manufacturers) || [];
      const prevManufacturers = (prevRestriction && prevRestriction.excluded_manufacturers) || [];
      const currentEnabled = currentRestriction && currentRestriction.export_restriction_marques;

      // If manufacturers were cleared and restriction is disabled
      if (prevManufacturers.length > 0 && currentManufacturers.length === 0 && !currentEnabled) {
        setIsRestrictionEnabled(false);
      }
    }
  }, [clientRestrictions, selectedPlatform]);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isDropdownOpen]);

  // Get restrictions for current platform
  const getRestrictionsByPlatform = useCallback(() => {
    if (!clientRestrictions || !Array.isArray(clientRestrictions)) {
      return null;
    }
    return clientRestrictions.find(
      (restriction) =>
        restriction.platform && Number(restriction.platform.id) === Number(selectedPlatform)
    );
  }, [clientRestrictions, selectedPlatform]);

  // Get current restrictions data
  const restrictions = useMemo(() => {
    const restrictionByPlatform = getRestrictionsByPlatform();
    return {
      exportRestrictionMarques:
        (restrictionByPlatform && restrictionByPlatform.export_restriction_marques) || false,
      excludedManufacturers:
        (restrictionByPlatform && restrictionByPlatform.excluded_manufacturers) || [],
    };
  }, [getRestrictionsByPlatform]);

  // Check if a manufacturer is selected
  const isManufacturerSelected = useCallback(
    (manufacturerId) => {
      return restrictions.excludedManufacturers.some((m) => m.id === manufacturerId);
    },
    [restrictions.excludedManufacturers]
  );

  // Filtered and sorted manufacturers with memoization
  const filteredManufacturers = useMemo(() => {
    const normalizedSearch = normalizeText(searchTerm);
    const filtered = (manufacturersAvailable || []).filter((manufacturer) =>
      normalizeText(manufacturer.name).includes(normalizedSearch)
    );

    // Sort: selected items first, then alphabetically
    return [...filtered].sort((a, b) => {
      const aSelected = restrictions.excludedManufacturers.some((m) => m.id === a.id);
      const bSelected = restrictions.excludedManufacturers.some((m) => m.id === b.id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return String(a.name || '').localeCompare(String(b.name || ''), 'fr');
    });
  }, [searchTerm, manufacturersAvailable, restrictions.excludedManufacturers]);

  // Update restriction helper
  const updateRestriction = useCallback(
    (updater, { createIfMissing = true } = {}) => {
      const clientId = Number(id);
      const platformId = Number(selectedPlatform);

      // Validate IDs before proceeding
      if (!Number.isFinite(clientId) || clientId <= 0) return false;
      if (!Number.isFinite(platformId) || platformId <= 0) return false;

      const restrictionByPlatform = getRestrictionsByPlatform();
      const restrictionsArray = Array.isArray(clientRestrictions) ? [...clientRestrictions] : [];

      if (restrictionByPlatform) {
        const updatedRestrictions = restrictionsArray.map((restriction) => {
          if (restriction.platform && Number(restriction.platform.id) === platformId) {
            const updated = {
              ...restriction,
              ...updater(restriction),
              client: { id: clientId },
              platform: { id: platformId },
            };
            // Explicitly preserve the id to prevent INSERT instead of UPDATE
            if (restriction.id) {
              updated.id = restriction.id;
            }
            return updated;
          }
          return restriction;
        });
        updateState('client_restrictions', updatedRestrictions);
        return true;
      }

      if (createIfMissing) {
        const newRestriction = {
          export_restriction_marques: false,
          excluded_manufacturers: [],
          ...updater(null),
          client: { id: clientId },
          platform: { id: platformId },
        };
        restrictionsArray.push(newRestriction);
        updateState('client_restrictions', restrictionsArray);
        return true;
      }

      return false;
    },
    [id, selectedPlatform, clientRestrictions, getRestrictionsByPlatform, updateState]
  );

  // Handle toggle restriction
  const handleToggleRestrictionMarques = useCallback(() => {
    const currentEnabled =
      isRestrictionEnabled != null
        ? isRestrictionEnabled
        : restrictions.exportRestrictionMarques || restrictions.excludedManufacturers.length > 0;
    const newEnabled = !currentEnabled;

    setIsRestrictionEnabled(newEnabled);

    // When toggling OFF, preserve the selected manufacturers in UI
    // to protect against accidental toggle (user can toggle back ON)
    // The backend will clear manufacturers when export_restriction_marques is false
    updateRestriction((restriction) => {
      const excludedManufacturers = (restriction && restriction.excluded_manufacturers) || [];
      return {
        export_restriction_marques: newEnabled ? excludedManufacturers.length > 0 : false,
        excluded_manufacturers: excludedManufacturers,
        _uiEnabled: newEnabled,
      };
    });
  }, [isRestrictionEnabled, restrictions, updateRestriction]);

  // Handle toggle manufacturer selection
  const handleToggleManufacturer = useCallback(
    (manufacturer) => {
      const isSelected = restrictions.excludedManufacturers.some((m) => m.id === manufacturer.id);

      const newExcludedManufacturers = isSelected
        ? restrictions.excludedManufacturers.filter((m) => m.id !== manufacturer.id)
        : [...restrictions.excludedManufacturers, { id: manufacturer.id, name: manufacturer.name }];

      const shouldExportRestrictionMarques = newExcludedManufacturers.length > 0;

      updateRestriction(() => ({
        export_restriction_marques: shouldExportRestrictionMarques,
        excluded_manufacturers: newExcludedManufacturers,
        _uiEnabled: true,
      }));

      // Keep UI enabled once user starts selecting
      if (isRestrictionEnabled !== true) {
        setIsRestrictionEnabled(true);
      }
    },
    [restrictions.excludedManufacturers, updateRestriction, isRestrictionEnabled]
  );

  // Handle remove manufacturer
  const handleRemoveManufacturer = useCallback(
    (manufacturerId) => {
      const newExcludedManufacturers = restrictions.excludedManufacturers.filter(
        (m) => m.id !== manufacturerId
      );
      const shouldExportRestrictionMarques = newExcludedManufacturers.length > 0;

      updateRestriction(
        () => ({
          export_restriction_marques: shouldExportRestrictionMarques,
          excluded_manufacturers: newExcludedManufacturers,
          _uiEnabled: true,
        }),
        { createIfMissing: false }
      );
    },
    [restrictions.excludedManufacturers, updateRestriction]
  );

  // Handle clear all manufacturers
  const handleClearAllManufacturers = useCallback(() => {
    openConfirmModal(
      'Supprimer toutes les marques',
      'Êtes-vous sûr de vouloir supprimer toutes les marques sélectionnées ? Cette action ne peut pas être annulée.',
      () => {
        updateRestriction(
          () => ({
            export_restriction_marques: false,
            excluded_manufacturers: [],
            _uiEnabled: false,
          }),
          { createIfMissing: false }
        );
        setIsRestrictionEnabled(false);
      }
    );
  }, [openConfirmModal, updateRestriction]);

  // Handle search input change
  const handleSearchInputChange = useCallback((e) => {
    setSearchInputValue(e.target.value);
    setFocusedIndex(-1);
  }, []);

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchInputValue('');
    setFocusedIndex(-1);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Scroll focused item into view
  // Accepts an optional index parameter to avoid stale closure issues
  const scrollToFocusedItem = useCallback(
    (index) => {
      const targetIndex = typeof index === 'number' ? index : focusedIndex;
      if (listContainerRef.current && targetIndex >= 0) {
        const items = listContainerRef.current.querySelectorAll('[role="option"]');
        if (items[targetIndex]) {
          items[targetIndex].scrollIntoView({ block: 'nearest' });
        }
      }
    },
    [focusedIndex]
  );

  // Handle keyboard navigation
  const handleSearchKeyDown = useCallback(
    (e) => {
      const itemCount = filteredManufacturers.length;
      const maxIndex = itemCount - 1;

      // Handle Escape regardless of results
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsDropdownOpen(false);
        setFocusedIndex(-1);
        return;
      }

      // Guard: no navigation when there are no results
      if (itemCount === 0) {
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => {
            const newIndex = Math.min(prev + 1, maxIndex);
            // Pass the new index directly to avoid stale closure
            setTimeout(() => scrollToFocusedItem(newIndex), 0);
            return newIndex;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => {
            const newIndex = Math.max(prev - 1, 0);
            // Pass the new index directly to avoid stale closure
            setTimeout(() => scrollToFocusedItem(newIndex), 0);
            return newIndex;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex <= maxIndex) {
            handleToggleManufacturer(filteredManufacturers[focusedIndex]);
          }
          break;
        default:
          break;
      }
    },
    [filteredManufacturers, focusedIndex, handleToggleManufacturer, scrollToFocusedItem]
  );

  // Derived values
  const selectedCount = restrictions.excludedManufacturers.length;
  const filteredCount = filteredManufacturers.length;
  const effectiveRestrictionEnabled =
    isRestrictionEnabled != null
      ? isRestrictionEnabled
      : restrictions.exportRestrictionMarques || selectedCount > 0;

  return (
    <div className="restrictions__grid">
      {/* Left column (1/3) */}
      <div className="page-profil__block-info">
        <div className="block-info__entete" style={{ display: 'flex', alignItems: 'center' }}>
          <Icon className="entete__icon" content="\e92e" />
          <span className="entete__label" style={{ fontSize: '13px' }}>
            Marque(s) à exclure du catalogue
          </span>
          {!readOnly && (
            <label className="switch" style={{ marginLeft: 'auto' }}>
              <input
                type="checkbox"
                name="export-restriction-marques"
                onClick={handleToggleRestrictionMarques}
                checked={effectiveRestrictionEnabled}
                readOnly
              />
              <span className="slider round" />
            </label>
          )}
        </div>

        {readOnly ? (
          <div className="block-info__content">
            {selectedCount > 0 ? (
              <span style={{ fontSize: '13px' }}>
                {selectedCount} marque{selectedCount > 1 ? 's' : ''} exclue
                {selectedCount > 1 ? 's' : ''}
              </span>
            ) : (
              <span style={{ color: '#999', fontSize: '13px' }}>Aucune marque exclue</span>
            )}
          </div>
        ) : (
          <>
            {effectiveRestrictionEnabled && (
              <div className="block-info__content">
                <div style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>
                  Sélectionner les marques à exclure du catalogue du client
                </div>

                {/* Dropdown selector */}
                <div ref={dropdownRef} style={{ position: 'relative' }}>
                  <div
                    role="combobox"
                    aria-haspopup="listbox"
                    aria-expanded={isDropdownOpen}
                    aria-label="Sélectionner les marques à exclure"
                    aria-controls="manufacturer-listbox"
                    tabIndex={0}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setIsDropdownOpen(!isDropdownOpen);
                      }
                      if (e.key === 'Escape' && isDropdownOpen) {
                        setIsDropdownOpen(false);
                      }
                    }}
                    style={{
                      width: '100%',
                      height: '38px',
                      padding: '8px 12px',
                      paddingRight: '30px',
                      border: '1px solid #ddd',
                      borderRadius: isDropdownOpen ? '4px 4px 0 0' : '4px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative',
                    }}
                  >
                    <span style={{ color: '#999', fontSize: '14px' }}>Sélectionner...</span>
                    <span
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: `translateY(-50%) rotate(${isDropdownOpen ? '180deg' : '0deg'})`,
                        transition: 'transform 0.2s',
                        fontSize: '10px',
                        color: '#666',
                      }}
                    >
                      ▼
                    </span>
                  </div>

                  {isDropdownOpen && (
                    <div
                      id="manufacturer-listbox"
                      role="listbox"
                      aria-label="Liste des marques"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderTop: 'none',
                        borderRadius: '0 0 4px 4px',
                        zIndex: 1000,
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      }}
                    >
                      {/* Search input with debounce and keyboard navigation */}
                      <div
                        style={{
                          padding: '8px',
                          borderBottom: '1px solid #eee',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <input
                          type="checkbox"
                          disabled
                          style={{ visibility: 'hidden', width: '16px' }}
                        />
                        <Icon content="\e93e" aria-hidden="true" />
                        <input
                          ref={searchInputRef}
                          type="text"
                          placeholder="Rechercher..."
                          aria-label="Rechercher une marque"
                          aria-controls="manufacturer-listbox"
                          aria-activedescendant={
                            focusedIndex >= 0 ? `manufacturer-option-${focusedIndex}` : undefined
                          }
                          value={searchInputValue}
                          onChange={handleSearchInputChange}
                          onKeyDown={handleSearchKeyDown}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            flex: 1,
                            padding: '4px 8px',
                            border: 'none',
                            outline: 'none',
                            fontSize: '13px',
                          }}
                        />
                        {searchInputValue && (
                          <button
                            type="button"
                            onClick={clearSearch}
                            aria-label="Effacer la recherche"
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '2px 6px',
                              fontSize: '14px',
                              color: '#999',
                              lineHeight: 1,
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>

                      {/* Result count */}
                      {searchTerm && !manufacturersLoading && !manufacturersError && (
                        <div
                          style={{
                            padding: '4px 12px',
                            fontSize: '11px',
                            color: '#666',
                            backgroundColor: '#f9f9f9',
                            borderBottom: '1px solid #eee',
                          }}
                        >
                          {filteredCount} résultat{filteredCount !== 1 ? 's' : ''}
                          {filteredCount > 0 && (
                            <span style={{ marginLeft: '8px', color: '#999' }}>
                              (↑↓ pour naviguer, Entrée pour sélectionner)
                            </span>
                          )}
                        </div>
                      )}

                      <div ref={listContainerRef} style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {manufacturersLoading && (
                          <div
                            style={{
                              padding: '12px',
                              textAlign: 'center',
                              color: '#999',
                              fontSize: '13px',
                            }}
                          >
                            Chargement...
                          </div>
                        )}

                        {!manufacturersLoading && manufacturersError && (
                          <div
                            style={{
                              padding: '12px',
                              textAlign: 'center',
                              color: '#d0021b',
                              fontSize: '13px',
                            }}
                          >
                            {manufacturersError}
                          </div>
                        )}

                        {!manufacturersLoading &&
                          !manufacturersError &&
                          filteredManufacturers.map((manufacturer, index) => {
                            const isSelected = isManufacturerSelected(manufacturer.id);
                            const isFocused = index === focusedIndex;
                            return (
                              <div
                                key={manufacturer.id}
                                id={`manufacturer-option-${index}`}
                                role="option"
                                aria-selected={isSelected}
                                tabIndex={-1}
                                onClick={() => handleToggleManufacturer(manufacturer)}
                                onMouseEnter={() => setFocusedIndex(index)}
                                style={{
                                  padding: '8px 12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  cursor: 'pointer',
                                  backgroundColor: isFocused
                                    ? '#e8e8e8'
                                    : isSelected
                                    ? '#e3f2fd'
                                    : 'white',
                                  borderBottom: '1px solid #f5f5f5',
                                  outline: isFocused ? '2px solid #1976d2' : 'none',
                                  outlineOffset: '-2px',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {}}
                                  aria-hidden="true"
                                  tabIndex={-1}
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    cursor: 'pointer',
                                    accentColor: '#1976d2',
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: '13px',
                                    color: isSelected ? '#1565c0' : '#333',
                                    fontWeight: isSelected ? '500' : 'normal',
                                  }}
                                >
                                  {highlightMatch(manufacturer.name, searchTerm)}
                                </span>
                              </div>
                            );
                          })}

                        {!manufacturersLoading &&
                          !manufacturersError &&
                          filteredManufacturers.length === 0 && (
                            <div
                              style={{
                                padding: '16px 12px',
                                textAlign: 'center',
                                color: '#666',
                                fontSize: '13px',
                              }}
                            >
                              <div style={{ marginBottom: '8px' }}>
                                {searchTerm ? (
                                  <>
                                    Aucune marque trouvée pour &quot;<strong>{searchTerm}</strong>
                                    &quot;
                                  </>
                                ) : (
                                  'Aucune marque disponible'
                                )}
                              </div>
                              {searchTerm && (
                                <button
                                  type="button"
                                  onClick={clearSearch}
                                  style={{
                                    background: '#f5f5f5',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    padding: '4px 12px',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    color: '#333',
                                  }}
                                >
                                  Effacer la recherche
                                </button>
                              )}
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                </div>

                {selectedCount > 0 && (
                  <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                    {selectedCount} marque{selectedCount > 1 ? 's' : ''} sélectionnée
                    {selectedCount > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Right column (2/3) - Selected brands */}
      {effectiveRestrictionEnabled && (
        <div className="page-profil__block-info">
          <div className="block-info__content" style={{ padding: '15px' }}>
            <div className="restrictions__selected-header">
              <span className="restrictions__selected-title">
                Marques sélectionnées
                {selectedCount > 0 && (
                  <span
                    style={{
                      marginLeft: '8px',
                      fontSize: '12px',
                      fontWeight: 'normal',
                      color: '#666',
                    }}
                  >
                    ({selectedCount} marque{selectedCount > 1 ? 's' : ''})
                  </span>
                )}
              </span>
              {!readOnly && selectedCount > 0 && (
                <span
                  role="button"
                  aria-label="Tout supprimer"
                  tabIndex={0}
                  className="restrictions__clear-all icon"
                  title="Tout supprimer"
                  onClick={handleClearAllManufacturers}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleClearAllManufacturers();
                    }
                  }}
                >
                  <Icon content="\e915" aria-hidden="true" />
                </span>
              )}
            </div>
            <div className="restrictions__keywords">
              {selectedCount > 0 ? (
                <>
                  {restrictions.excludedManufacturers.map((manufacturer) => (
                    <div key={manufacturer.id}>
                      <span>{manufacturer.name}</span>
                      {!readOnly && (
                        <span
                          role="button"
                          aria-label={`Supprimer ${manufacturer.name}`}
                          tabIndex={0}
                          className="icon"
                          onClick={() => handleRemoveManufacturer(manufacturer.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleRemoveManufacturer(manufacturer.id);
                            }
                          }}
                        >
                          <Icon content="\e915" aria-hidden="true" />
                        </span>
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <span style={{ color: '#999', fontSize: '13px', fontStyle: 'italic' }}>
                  Aucune marque sélectionnée
                </span>
              )}
            </div>
          </div>
        </div>
      )}
      {!effectiveRestrictionEnabled && <div className="restrictions__placeholder" />}
    </div>
  );
};

Restrictions.propTypes = {
  readOnly: PropTypes.bool,
  id: PropTypes.number,
  clientRestrictions: PropTypes.array,
  updateState: PropTypes.func,
  // Redux props
  selectedPlatform: PropTypes.number,
  manufacturersAvailable: PropTypes.array,
  manufacturersLoading: PropTypes.bool,
  manufacturersError: PropTypes.string,
  fetchClientManufacturers: PropTypes.func,
  openConfirmModal: PropTypes.func,
};

Restrictions.defaultProps = {
  readOnly: false,
  id: null,
  clientRestrictions: [],
  updateState: () => {},
  // Redux defaults
  selectedPlatform: null,
  manufacturersAvailable: [],
  manufacturersLoading: false,
  manufacturersError: null,
  fetchClientManufacturers: () => {},
  openConfirmModal: () => {},
};

const mapStateToProps = (state, ownProps) => {
  const { platform, client } = state;
  const { manufacturers } = client;
  const selectedPlatform = Number(platform.selectedId) || null;
  const clientId = Number(ownProps.id);
  const hasClientId = Number.isFinite(clientId) && clientId > 0;
  const platformKey = Number.isFinite(selectedPlatform) ? String(selectedPlatform) : null;
  const manufacturersByPlatform = (manufacturers && manufacturers.byPlatformId) || {};

  // Prefer cached entry for the selected platform; fallback supports legacy state shape.
  const platformEntry = platformKey ? manufacturersByPlatform[platformKey] : null;
  const legacyEntry = manufacturers && manufacturers.items ? manufacturers : null;
  const entry = platformEntry || legacyEntry;

  const isCurrentRequest =
    hasClientId &&
    entry &&
    Number(entry.platformId) === Number(selectedPlatform) &&
    Number(entry.clientId) === Number(clientId);

  return {
    selectedPlatform,
    manufacturersAvailable: isCurrentRequest ? entry.items || [] : [],
    manufacturersLoading: isCurrentRequest ? entry.isLoading || false : false,
    manufacturersError: isCurrentRequest ? entry.error || null : null,
  };
};

const mapDispatchToProps = (dispatch) => ({
  fetchClientManufacturers: (clientId, platformId) =>
    dispatch(clientActions.fetchClientManufacturers(clientId, platformId)),
  openConfirmModal: (title, message, callback) =>
    dispatch(modalActions.open('confirm', { title, message, callback })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Restrictions);
